import React, { useState } from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";
import { toast } from "sonner";
import {
  AlertTriangle,
  Banknote,
  Download,
  HandCoins,
  Package,
  Printer,
  Receipt as ReceiptIcon,
  ShoppingBag,
  TrendingUp,
  Wallet,
} from "lucide-react";
import { api, apiError, downloadPdf } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { useI18n } from "@/contexts/I18nContext";
import { StatCard, Section, EmptyState, LoadError } from "@/components/Primitives";
import { StaggerRoot, StaggerItem } from "@/components/motion";

const COLORS = ["#071A33", "#2563EB", "#F59E0B", "#DC2626", "#16A34A", "#7C3AED"];

export default function ReportDaily() {
  const { t, money, lang } = useI18n();
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["report-daily", date],
    queryFn: () => api.get("/reports/daily", { params: { date } }).then((r) => r.data),
    retry: 1,
  });

  const handleDownloadPdf = async () => {
    try {
      await downloadPdf(`/reports/daily.pdf?date=${date}&lang=${lang}`, `daily-${date}.pdf`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (isLoading) return <div className="p-10 text-gray-500">{t("loading")}</div>;
  if (isError) return <LoadError message={apiError(error)} onRetry={() => refetch()} />;

  return (
    <StaggerRoot className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 font-heading">{t("dailySales")}</h1>
          <p className="text-sm text-gray-500 mt-1">{date}</p>
        </div>
        <div className="flex gap-2">
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="h-11 px-3 rounded-xl border border-gray-200 bg-white text-sm" data-testid="report-date" />
          <button data-testid="daily-pdf" onClick={handleDownloadPdf} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#071A33] text-white text-sm font-medium hover:bg-[#7F1020]">
            <Download className="w-4 h-4" /> PDF
          </button>
          <button onClick={() => window.print()} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-gray-200 text-sm font-medium hover:bg-gray-50">
            <Printer className="w-4 h-4" /> {t("print")}
          </button>
        </div>
      </div>

      <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard icon={Banknote} label={lang === "pashto" ? "ټول خرڅلاو" : "فروش ناخالص"} value={money(data.gross_sales ?? data.total_sales)} accent="#071A33" />
            <StatCard icon={Wallet} label={lang === "pashto" ? "نقد ترلاسه شوی" : "نقد دریافت‌شده"} value={money(data.cash_collected || 0)} accent="#16A34A" />
            <StatCard icon={ShoppingBag} label={t("ordersToday")} value={data.total_orders} accent="#2563EB" />
            <StatCard icon={AlertTriangle} label={t("debt")} value={money(data.debt_created ?? data.total_debt)} accent="#F97316" />
            <StatCard icon={HandCoins} label={lang === "pashto" ? "پور ترلاسه شوی" : "قرض دریافت‌شده"} value={money(data.debt_collected || 0)} accent="#7C3AED" />
            <StatCard icon={ReceiptIcon} label={t("expenses")} value={money(data.expenses || 0)} accent="#DC2626" />
            <StatCard icon={Package} label="COGS" value={money(data.cogs || 0)} accent="#64748B" />
            <StatCard icon={TrendingUp} label={t("netProfit")} value={money(data.net_profit || 0)} accent={(data.net_profit || 0) < 0 ? "#DC2626" : "#16A34A"} />
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <Section title={t("hourlyChart")}>
              <div style={{ width: "100%", height: 260 }}>
                <ResponsiveContainer>
                  <BarChart data={data.hourly}>
                    <CartesianGrid stroke="#f3f4f6" vertical={false} />
                    <XAxis dataKey="hour" stroke="#9ca3af" fontSize={11} />
                    <YAxis stroke="#9ca3af" fontSize={11} />
                    <Tooltip formatter={(v) => money(v)} contentStyle={{ borderRadius: 12, fontSize: 12, direction: "rtl" }} />
                    <Bar dataKey="amount" fill="#071A33" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Section>

            <Section title={t("byMethod")}>
              {data.by_method.length === 0 ? <EmptyState title={t("empty")} /> : (
                <div style={{ width: "100%", height: 260 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie data={data.by_method} dataKey="amount" nameKey="method" cx="50%" cy="50%" outerRadius={90} label={(d) => t(d.method)}>
                        {data.by_method.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v) => money(v)} contentStyle={{ borderRadius: 12, fontSize: 12, direction: "rtl" }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Section>
          </div>

          <Section title={t("byCategory")}>
            {data.by_category.length === 0 ? <EmptyState title={t("empty")} /> : (
              <div style={{ width: "100%", height: 260 }}>
                <ResponsiveContainer>
                  <BarChart data={data.by_category} layout="vertical">
                    <CartesianGrid stroke="#f3f4f6" horizontal={false} />
                    <XAxis type="number" stroke="#9ca3af" fontSize={11} />
                    <YAxis type="category" dataKey="category" stroke="#9ca3af" fontSize={12} width={120} />
                    <Tooltip formatter={(v) => money(v)} contentStyle={{ borderRadius: 12, fontSize: 12, direction: "rtl" }} />
                    <Bar dataKey="amount" fill="#071A33" radius={[0, 6, 6, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Section>

          <Section title={t("recentOrders")}>
            {data.orders.length === 0 ? <EmptyState title={t("empty")} /> : (
              <div className="overflow-x-auto thin-scroll">
                <table className="w-full text-right text-sm">
                  <thead className="text-xs text-gray-500 bg-gray-50">
                    <tr>
                      <th className="p-3">#</th>
                      <th className="p-3">{t("date")}</th>
                      <th className="p-3">{t("waiter")}</th>
                      <th className="p-3">{t("paymentMethod")}</th>
                      <th className="p-3">{t("total")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.orders.map((o) => (
                      <tr key={o.id} className="border-t border-gray-100">
                        <td className="p-3 font-mono text-xs text-gray-600">{o.order_number}</td>
                        <td className="p-3 text-gray-500 tabular">{(o.paid_at || o.created_at).slice(11, 16)}</td>
                        <td className="p-3 text-gray-700">{o.waiter_name}</td>
                        <td className="p-3">{t(o.payment_method || "cash")}</td>
                        <td className="p-3 tabular font-bold">{money(o.paid_amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Section>
        </>
    </StaggerRoot>
  );
}
